// src/screens/TeamStats.tsx
import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { BarChart2 } from "lucide-react";
import i18n from "../i18n";
import { LogoLoader } from "../components/LogoLoader";
import { BackButton } from "../components/BackButton";

// ---- mini hook i18n "safe" (évite TS2589) ----
function useI18nSafe() {
  const [lng, setLng] = useState((i18n.resolvedLanguage || i18n.language || "fr").toLowerCase());
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

// ---- Types ----
type StatBlock = {
  wins: number;
  losses: number;
  pct: number;
  pf: number;
  pa: number;
  gamesWithScore: number;
  avgPf: number;
  avgPa: number;
  diff: number;
};
type TeamStatsShape = { total: StatBlock; home: StatBlock; away: StatBlock };

const emptyBlock: StatBlock = {
  wins: 0, losses: 0, pct: 0, pf: 0, pa: 0, gamesWithScore: 0, avgPf: 0, avgPa: 0, diff: 0,
};
const emptyStats: TeamStatsShape = { total: { ...emptyBlock }, home: { ...emptyBlock }, away: { ...emptyBlock } };

type MatchRow = {
  victory: boolean | null;
  location: "domicile" | "extérieur" | "home" | "away" | string | null;
  team_id: string | null;
  score?: string | null;
};

type TeamLite = { id: string; team_name: string | null };

// ---- Utils ----
const isAway = (loc?: string | null) => {
  const v = (loc || "").toLowerCase();
  return v === "extérieur" || v === "exterieur" || v === "away";
};
const isHome = (loc?: string | null) => {
  const v = (loc || "").toLowerCase();
  return v === "domicile" || v === "home";
};

const parseScore = (
  score: string | null | undefined,
  location: string | null | undefined,
  victory: boolean | null | undefined
): { team: number; opp: number } | null => {
  if (!score) return null;
  const m = score.match(/(\d+)\s*[-–—:]\s*(\d+)/);
  if (!m) return null;
  const a = parseInt(m[1], 10);
  const b = parseInt(m[2], 10);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;

  let team = isAway(location) ? b : a;
  let opp = isAway(location) ? a : b;

  // si victory est renseigné et contredit le sens deduit par location/score, on inverse
  if (typeof victory === "boolean") {
    const teamWon = team > opp;
    if (teamWon !== victory) [team, opp] = [opp, team];
  }
  return { team, opp };
};

const foldStats = (rows: MatchRow[]): StatBlock => {
  const wins = rows.filter((m) => m.victory === true).length;
  const losses = rows.filter((m) => m.victory === false).length;
  const gamesWL = wins + losses;
  const pct = gamesWL > 0 ? Math.round((wins / gamesWL) * 100) : 0;

  let pf = 0, pa = 0, gamesWithScore = 0;
  for (const m of rows) {
    const parsed = parseScore(m.score ?? null, m.location ?? null, m.victory ?? null);
    if (parsed) { pf += parsed.team; pa += parsed.opp; gamesWithScore++; }
  }
  const avgPf = gamesWithScore > 0 ? Math.round((pf / gamesWithScore) * 10) / 10 : 0;
  const avgPa = gamesWithScore > 0 ? Math.round((pa / gamesWithScore) * 10) / 10 : 0;
  const diff = pf - pa;

  return { wins, losses, pct, pf, pa, gamesWithScore, avgPf, avgPa, diff };
};

// ---- Component ----
export const TeamStats = () => {
  const { t } = useI18nSafe();
  const [teams, setTeams] = useState<TeamLite[]>([]);
  const [selectedTeamIndex, setSelectedTeamIndex] = useState(0);
  const [stats, setStats] = useState<TeamStatsShape>(emptyStats);
  const [loading, setLoading] = useState(true);

  // --- charge les équipes visibles par l'utilisateur (owner OU membre actif)
  useEffect(() => {
    const fetchTeams = async () => {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setTeams([]); setLoading(false); return; }

      const uid = user.id;

      // A) équipes dont il est propriétaire
      const { data: owned, error: ownedErr } = await supabase
        .from("teams")
        .select("id, team_name")
        .eq("user_id", uid)
        .or("archived.is.null,archived.eq.false");

      if (ownedErr) console.error("[teamstats] owned teams error:", ownedErr);

      // B) équipes où il est membre actif (via team_members)
      //   On exploite la relation FK team_members.team_id -> teams.id si disponible
      const { data: memberRows, error: memberErr } = await supabase
        .from("team_members")
        .select("team_id, status, teams!inner(id, team_name, archived)")
        .eq("user_id", uid)
        .eq("status", "active");

      if (memberErr) console.error("[teamstats] member teams error:", memberErr);

      const memberTeams: TeamLite[] = (memberRows || [])
        .map((r: any) => r.teams)
        .filter((t: any) => t && t.id && !t.archived);

      // merge + unique
      const byId = new Map<string, TeamLite>();
      (owned || []).forEach((t) => byId.set(t.id, { id: t.id, team_name: t.team_name }));
      memberTeams.forEach((t) => byId.set(t.id, { id: t.id, team_name: t.team_name }));

      const merged = Array.from(byId.values()).sort((a, b) =>
        (a.team_name || "").localeCompare(b.team_name || "")
      );

      setTeams(merged);
      // Si l’index sélectionné sort des limites, on le remet à 0
      if (merged.length === 0) setSelectedTeamIndex(0);
      else if (selectedTeamIndex >= merged.length) setSelectedTeamIndex(0);

      setLoading(false);
    };
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- charge les stats de l'équipe sélectionnée
  useEffect(() => {
    const fetchStats = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || teams.length === 0) { setStats(emptyStats); return; }

      const selectedTeam = teams[selectedTeamIndex];
      if (!selectedTeam?.id) { setStats(emptyStats); return; }

      setLoading(true);

      // 👉 IMPORTANT: on NE filtre plus par user_id ici (sinon les membres ne voient rien)
      const { data: matches, error } = await supabase
        .from("matches")
        .select("victory, location, team_id, score")
        .eq("team_id", selectedTeam.id);

      if (error) {
        console.error("[teamstats] matches error:", error);
        setStats(emptyStats);
        setLoading(false);
        return;
      }

      const list = (matches || []) as MatchRow[];
      const totalStats = foldStats(list);
      const homeStats = foldStats(list.filter((m) => isHome(m.location)));
      const awayStats = foldStats(list.filter((m) => isAway(m.location)));

      setStats({ total: totalStats, home: homeStats, away: awayStats });
      setLoading(false);
    };
    fetchStats();
  }, [teams, selectedTeamIndex]);

  if (loading) {
    return <LogoLoader />;
  }

  return (
    <section className="flex flex-col items-center min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 transition-colors">
      {/* Header mobile sticky */}
      <div className="sticky top-0 z-50 w-full bg-gray-50/95 dark:bg-gray-950/95 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        <div className="relative max-w-3xl mx-auto px-4 py-3 pt-[calc(env(safe-area-inset-top,0px)+0.75rem)]">
          {/* Bouton retour */}
          <div className="absolute left-4 top-1/2 -translate-y-1/2">
            <BackButton />
          </div>

          {/* Titre centré */}
          <h1 className="text-lg font-bold text-orange-600 dark:text-orange-400 flex items-center justify-center gap-2 text-center">
            <BarChart2 className="w-5 h-5" />
            {t("teamstats:title")}
          </h1>
        </div>
      </div>
      {teams.length > 0 ? (
        <div className="mb-6">
          <label htmlFor="team-select" className="mr-2 font-medium text-gray-700 dark:text-gray-300">
            {t("teamstats:select_team")}
          </label>
          <select
            id="team-select"
            value={selectedTeamIndex}
            onChange={(e) => setSelectedTeamIndex(parseInt(e.target.value, 10))}
            className="border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900
                       text-gray-900 dark:text-gray-100 px-3 py-2 rounded
                       focus:outline-none focus:ring-2 focus:ring-orange-400 transition-colors"
          >
            {teams.map((team, idx) => (
              <option key={team.id} value={idx}>
                {team.team_name || t("teamstats:team_option_fallback", { index: idx + 1 })}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className="mb-6 text-sm text-gray-700 dark:text-gray-300">
          {t("teamstats:no_team")}
        </div>
      )}

      <div className="overflow-x-auto w-full max-w-3xl rounded-xl border border-gray-200 dark:border-gray-800">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 dark:bg-gray-800/80">
            <tr className="text-left">
              <th className="p-3">{t("teamstats:table.category")}</th>
              <th className="p-3 text-center">{t("teamstats:table.wins")}</th>
              <th className="p-3 text-center">{t("teamstats:table.losses")}</th>
              <th className="p-3 text-center">{t("teamstats:table.win_pct")}</th>
              <th className="p-3 text-center">{t("teamstats:table.pf")}</th>
              <th className="p-3 text-center">{t("teamstats:table.pa")}</th>
              <th className="p-3 text-center">{t("teamstats:table.diff")}</th>
              <th className="p-3 text-center">{t("teamstats:table.avg_pf")}</th>
              <th className="p-3 text-center">{t("teamstats:table.avg_pa")}</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900">
            <tr className="border-t border-gray-200 dark:border-gray-800">
              <td className="p-3 font-medium">{t("teamstats:rows.total")}</td>
              <td className="p-3 text-center">{stats.total.wins}</td>
              <td className="p-3 text-center">{stats.total.losses}</td>
              <td className="p-3 text-center">{stats.total.pct} %</td>
              <td className="p-3 text-center">{stats.total.pf}</td>
              <td className="p-3 text-center">{stats.total.pa}</td>
              <td className="p-3 text-center">{stats.total.diff}</td>
              <td className="p-3 text-center">{stats.total.avgPf}</td>
              <td className="p-3 text-center">{stats.total.avgPa}</td>
            </tr>
            <tr className="border-t border-gray-200 dark:border-gray-800">
              <td className="p-3 font-medium">{t("teamstats:rows.home")}</td>
              <td className="p-3 text-center">{stats.home.wins}</td>
              <td className="p-3 text-center">{stats.home.losses}</td>
              <td className="p-3 text-center">{stats.home.pct} %</td>
              <td className="p-3 text-center">{stats.home.pf}</td>
              <td className="p-3 text-center">{stats.home.pa}</td>
              <td className="p-3 text-center">{stats.home.diff}</td>
              <td className="p-3 text-center">{stats.home.avgPf}</td>
              <td className="p-3 text-center">{stats.home.avgPa}</td>
            </tr>
            <tr className="border-t border-gray-200 dark:border-gray-800">
              <td className="p-3 font-medium">{t("teamstats:rows.away")}</td>
              <td className="p-3 text-center">{stats.away.wins}</td>
              <td className="p-3 text-center">{stats.away.losses}</td>
              <td className="p-3 text-center">{stats.away.pct} %</td>
              <td className="p-3 text-center">{stats.away.pf}</td>
              <td className="p-3 text-center">{stats.away.pa}</td>
              <td className="p-3 text-center">{stats.away.diff}</td>
              <td className="p-3 text-center">{stats.away.avgPf}</td>
              <td className="p-3 text-center">{stats.away.avgPa}</td>
            </tr>
          </tbody>
        </table>
        <div className="p-3 text-xs text-gray-500 dark:text-gray-400">
          {t("teamstats:footnote", { count: stats.total.gamesWithScore })}
        </div>
      </div>
    </section>
  );
};
